import React from "react";
import { connect } from "react-redux";
import { Field, InjectedFormProps, reduxForm } from "redux-form";
import { Form, Message, Modal } from "semantic-ui-react";
import { doesNotExist } from "../../utils/utils";
import DropdownInput from "../forms/DropdownInput";
import LabeledInput from "../forms/LabeledInput";

type SelectSubscriptionModalProps = {
  /** Modal state. True == open .*/
  openModal: boolean;
  /** Function to run on modal close. */
  onModalClose: () => void;
  /** Default subscription, if any. */
  defaultSubscription?: string;
  /** Subscription Options, if any. */
  subscriptionOptions?: unknown[];
  /** Function to call when submitting form. */
  onSubmit: (values: any) => void;
  /** Reflects onSubmit form error state. This works nice with fathers' Redux state management. */
  formSubmitFailed: boolean;
  /** Message to show on onSubmit error. */
  formSubmitFailedMessage: string;
  /** Should the form display Loading component. */
  isLoading: boolean;
  /** Is upgrading from another subscription?. */
  isUpgrading?: boolean;
  /** Proration cost (only if isUpgrading) */
  prorationCost?: number | null;
  /** Calculate proration cost function (only if isUpgrading) */
  calculateProrationCost?: (planId: string) => void;
  /** Is getting proration cost? (only if isUpgrading) */
  gettingProrationCost?: boolean;

  selectedSubscription?: string;
};

type SelectSubscriptionModalState = {
  disableConfirm?: boolean;
};

/**
 * Modal with form for allowing user to add a new Subscription to account.
 */
class SelectSubscriptionModal extends React.PureComponent<
  SelectSubscriptionModalProps & InjectedFormProps<{}, SelectSubscriptionModalProps>,
  SelectSubscriptionModalState
> {
  state = {
    disableConfirm: this.props.isUpgrading
  };

  UNSAFE_componentWillMount() {
    this.props.initialize({ selectedSubscription: this.props.defaultSubscription });
  }

  _onClose = () => {
    this.props.onModalClose();
  };

  _validateSubscription = (value: string) =>
    value === this.props.defaultSubscription ? "Must select a different subscription." : undefined;

  _disable = () => this.props.isUpgrading && this.props.gettingProrationCost;

  // Value should be a string, but there's a comparison with 0
  _calculateProration = (value: any) => {
    if (value !== 0 && this.props.isUpgrading) this.props.calculateProrationCost?.(value);
    this.setState({
      disableConfirm: value === this.props.defaultSubscription
    });
  };

  componentDidUpdate(prevProps: SelectSubscriptionModalProps) {
    if (prevProps.prorationCost !== this.props.prorationCost)
      this.props.change("prorationCost", this.props.prorationCost);
  }

  _renderUpgradeCost = () => {
    if (!this.props.isUpgrading || doesNotExist(this.props.prorationCost)) return null;
    return (
      <Field
        component={LabeledInput}
        name="prorationCost"
        icon="dollar"
        label="Subscription upgrade cost"
        onChange={(evt: any) => evt.preventDefault()}
      />
    );
  };

  render() {
    return (
      <Modal dimmer="blurring" size="tiny" open={this.props.openModal} onClose={this._onClose}>
        <Modal.Header>Select subscription</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Form onSubmit={this.props.handleSubmit} size="large" loading={this.props.isLoading}>
              <Field
                component={DropdownInput}
                name="selectedSubscription"
                placeholder="Select subscription"
                fluid
                selection
                options={this.props.subscriptionOptions}
                defaultValue={this.props.selectedSubscription}
                validate={this._validateSubscription}
                disabled={this._disable()}
                onChange={this._calculateProration}
              />
              {this._renderUpgradeCost()}
              {this.props.isUpgrading ? (
                <Message info>
                  The same credit card used for the current subscription will be charged
                </Message>
              ) : null}
              <Form.Button
                primary
                disabled={
                  this._disable() ||
                  (this.props.prorationCost && this.props.prorationCost <= 0) ||
                  this.state.disableConfirm
                }
              >
                Confirm
              </Form.Button>
            </Form>
            {this.props.formSubmitFailed ? (
              <Message negative>{this.props.formSubmitFailedMessage}</Message>
            ) : (
              <div />
            )}
          </Modal.Description>
        </Modal.Content>
      </Modal>
    );
  }
}

const SelectSubscriptionFormModal = reduxForm<{}, SelectSubscriptionModalProps>({
  form: "selectSubscription"
})(SelectSubscriptionModal);

export default connect()(SelectSubscriptionFormModal);
